/**
 * Modal — direct port of `.modal` + `.modal-backdrop` from the prototype
 * (cms-redesign-mockup.html ~line 2295–2384). Reusable for any
 * confirm / edit / picker dialog that needs the brand's centered card
 * with eyebrow + title + close + body + footer.
 *
 *   <Modal
 *     open={open}
 *     onClose={() => setOpen(false)}
 *     eyebrow="Quick Taps"
 *     title="In-app intro text"
 *     maxWidth={560}
 *     footer={
 *       <>
 *         <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
 *         <Button variant="primary" onClick={save}>Save</Button>
 *       </>
 *     }
 *   >
 *     {…body…}
 *   </Modal>
 *
 * Behavior:
 *   • Closes on backdrop click + Escape + close-button click.
 *   • Animates in via the same `cmdPaletteIn` keyframe used by the ⌘K
 *     palette — opacity + translate + scale.
 *   • Body scrolls if content overflows the 80vh max-height.
 *   • Rendered inline inside #root (not via React portal) so Tailwind
 *     utilities scoped via `important: '#root'` apply to its tree.
 */
import React, { useEffect } from 'react';
import BrandIcon from './BrandIcon';
import { cn } from './cn';

export default function Modal({
  open,
  onClose,
  eyebrow,
  title,
  children,
  footer,
  maxWidth = 560,
  // ARIA label override for the close button.
  closeLabel = 'Close',
}) {
  /* Esc closes. Attached only while open so we don't add a global
   * listener for nothing. */
  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose?.();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  /* Lock body scroll while the modal is open. */
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  return (
    // `.modal-backdrop` (2295–2304): fixed inset 0, navy/40 + 2px blur,
    // z-index 300, grid place-items center, padding 24.
    <div
      onClick={(e) => {
        // Close only when clicking the backdrop itself, not the modal.
        if (e.target === e.currentTarget) onClose?.();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: '#2C3E5066',
        backdropFilter: 'blur(2px)',
        WebkitBackdropFilter: 'blur(2px)',
        zIndex: 300,
        padding: 24,
        display: 'grid',
        placeItems: 'center',
        animation: 'fadeIn 160ms ease forwards',
      }}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title || 'Dialog'}
        className="bg-surface border border-sand flex flex-col"
        style={{
          width: '100%',
          maxWidth,
          borderRadius: 12,
          maxHeight: '80vh',
          boxShadow: '0 24px 60px #2C3E5033, 0 4px 12px #2C3E501A',
          overflow: 'hidden',
          // Same entrance as the ⌘K palette — opacity + translate + scale.
          animation: 'cmdPaletteIn 180ms cubic-bezier(0.4, 0, 0.2, 1) forwards',
        }}
      >
        {/* `.modal-head` (2322–2353): 16/20 padding, sand bottom border. */}
        {(title || eyebrow || onClose) && (
          <div
            className="flex items-center justify-between border-b border-sand shrink-0"
            style={{ padding: '16px 20px' }}
          >
            <div className="min-w-0">
              {eyebrow && (
                <div
                  className="font-condensed font-bold uppercase text-text-muted"
                  style={{
                    fontSize: 10.5,
                    letterSpacing: '0.08em',
                    marginBottom: 2,
                  }}
                >
                  {eyebrow}
                </div>
              )}
              {title && (
                <h2
                  className="font-condensed font-bold text-navy"
                  style={{ fontSize: 18, letterSpacing: '-0.01em', margin: 0 }}
                >
                  {title}
                </h2>
              )}
            </div>
            {onClose && (
              // `.modal-close` (2340–2353): 32×32 ghost, cream hover, brand close glyph.
              <button
                type="button"
                onClick={onClose}
                aria-label={closeLabel}
                className={cn(
                  'grid place-items-center text-text-muted shrink-0',
                  'bg-transparent border-0 cursor-pointer rounded-md',
                  'hover:bg-cream hover:text-navy',
                )}
                style={{
                  width: 32,
                  height: 32,
                  transition: 'all 140ms ease',
                }}
              >
                <BrandIcon name="close" size={12} />
              </button>
            )}
          </div>
        )}

        {/* `.modal-body` (2354–2357): 18/20 padding, flex 1, scrolls. */}
        <div className="flex-1 overflow-y-auto" style={{ padding: '18px 20px' }}>
          {children}
        </div>

        {/* `.modal-foot` (2378–2384): right-aligned actions, cream bg,
         *  sand top border. Renders only when caller supplies `footer`. */}
        {footer && (
          <div
            className="flex items-center justify-end bg-cream border-t border-sand shrink-0"
            style={{ gap: 8, padding: '14px 20px' }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
